import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingLiveArea } from '@euler/model/coaching-practice';
import { memo, useMemo } from 'react';
import { View } from 'react-native';
import { aiColors } from '../../../../ai-suite/theme';
import { Chip } from '../../../components/Chip';
import { AreaRadar } from './AreaRadar';
import { AreaScoreBar } from './AreaScoreBar';
import { ReportSection } from './ReportSection';

/** Fewer areas than this read better as bars alone. */
const kRadarMinAreas = 3;

const kCounts = ['', '一', '两', '三', '四', '五', '六', '七', '八'];

/**
 * The assessed areas as bars beside their radar (from three areas); the
 * last attempt at this case and level shows behind as a dashed outline.
 */
export const AreaSection = memo(
  ({
    areas,
    previous,
    wide,
  }: {
    areas: CoachingLiveArea[];
    previous: CoachingLiveArea[] | null;
    wide: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const before = useMemo(
      () => new Map((previous ?? []).map(x => [x.area, x.score])),
      [previous],
    );
    const average = (list: number[]) =>
      list.reduce((sum, x) => sum + x, 0) / Math.max(list.length, 1);
    const shared = areas.filter(x => before.has(x.area));
    const change = shared.length
      ? Math.round(
          average(shared.map(x => x.score)) -
            average(shared.map(x => before.get(x.area)!)),
        )
      : null;
    const radar = areas.length >= kRadarMinAreas;
    return (
      <ReportSection
        title={`${kCounts[areas.length] ?? areas.length}个能力`}
        trailing={
          change != null ? (
            <Chip
              text={`比上次 ${change >= 0 ? '+' : ''}${change}`}
              color={change >= 0 ? aiColors.green : aiColors.amber}
            />
          ) : null
        }
      >
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(16) }}
        >
          {radar ? (
            <AreaRadar
              scores={areas.map(x => x.score)}
              previous={
                // An area new since then draws where it stands now.
                previous ? areas.map(x => before.get(x.area) ?? x.score) : null
              }
              size={rpx(wide ? 200 : 128)}
            />
          ) : null}
          <View style={{ flex: 1, minWidth: 0, gap: rpx(10) }}>
            {areas.map(x => (
              <AreaScoreBar key={x.area} area={x} />
            ))}
          </View>
        </View>
      </ReportSection>
    );
  },
);
